import { CategoryId } from '@business/entities/category';
import { ServiceAPI } from '@business/api/service';
import { ProjectServiceResponse, ProjectServiceType } from '@business/entities/project';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap, tap } from 'rxjs/operators';
import { SettingsConfig } from '@core/utils/config';

export class ServiceBloc extends BaseBloc {
  private readonly serviceRepo = new Repository<ProjectServiceResponse>({
    getItemId: service => service.id,
  });

  services = {
    all$: this.serviceRepo.items$.pipe(map(items => R.sortBy(items, item => item.title))),
    active$: this.serviceRepo.items$.pipe(
      map(items =>
        R.pipe(
          items,
          R.ops.filter(s => s.isActive),
          R.ops.sortBy(s => s.title),
        ),
      ),
    ),
    project$: this.serviceRepo.items$.pipe(
      map(items =>
        R.pipe(
          items,
          R.ops.filter(s => s.isActive),
          R.ops.filter(s => s.type === ProjectServiceType.project),
          R.ops.sortBy(s => s.title),
        ),
      ),
    ),
    subscription$: this.serviceRepo.items$.pipe(
      map(items =>
        R.pipe(
          items,
          R.ops.filter(s => s.isActive),
          R.ops.filter(s => s.type === ProjectServiceType.marketing),
          R.ops.sortBy(s => s.title),
        ),
      ),
    ),
    subscriptionRecurring$: this.serviceRepo.items$.pipe(
      map(items =>
        R.pipe(
          items,
          R.ops.filter(s => s.isActive),
          R.ops.filter(s => s.type === ProjectServiceType.marketing || s.type === ProjectServiceType.recurring),
          R.ops.sortBy(s => s.title),
        ),
      ),
    ),
    recurring$: this.serviceRepo.items$.pipe(
      map(items =>
        R.pipe(
          items,
          R.ops.filter(s => s.isActive),
          R.ops.filter(s => s.type === ProjectServiceType.recurring),
          R.ops.sortBy(s => s.title),
        ),
      ),
    ),
  };

  readonly selectService = this.serviceRepo.selectItem;

  onReset() {
    this.serviceRepo.reset();
  }

  fetchServices = (isPublic?: boolean, isActive = false) => {
    return ServiceAPI.fetchServices(isPublic, isActive).pipe(
      this.serviceRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_service_error') }),
    );
  };

  fetchService = (id: string) => {
    return ServiceAPI.fetchService(id).pipe(
      this.serviceRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_service_error') }),
    );
  };

  createService = (service: Partial<ProjectServiceResponse>) => {
    return ServiceAPI.createService(service).pipe(
      this.serviceRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_service'),
      }),
    );
  };

  updateService = (id: string, service: Partial<ProjectServiceResponse>) => {
    return ServiceAPI.updateService(id, service).pipe(
      this.serviceRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_service'),
      }),
    );
  };

  deleteService = (serviceId: string, makingActive = false) => {
    if (makingActive) {
      return ServiceAPI.deleteService(serviceId).pipe(
        this.serviceRepo.ops.upsertOne(item => ({ item })),
        handleMessage({
          type: requestMessage('active_service'),
        }),
      );
    }
    return showDeleteConfirmation('Inactive Service', 'Do you really want to make this service inactive?').pipe(
      switchMap(() => ServiceAPI.deleteService(serviceId)),
      this.serviceRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('deactive_service'),
      }),
    );
  };

  setMarketingServices = (ids: string[]) => {
    return ServiceAPI.setMarketingServices(ids).pipe(
      this.serviceRepo.ops.reset(),
      handleMessage({
        type: requestMessage('marketing_services'),
      }),
    );
  };

  changeType = (id: string, type: ProjectServiceType) => {
    return ServiceAPI.changeType(id, type).pipe(this.serviceRepo.ops.upsertOne(item => ({ item })));
  };

  updateCategories = (id: string, ids: CategoryId[]) => {
    return ServiceAPI.updateCategories(id, ids).pipe(this.serviceRepo.ops.upsertOne(item => ({ item })));
  };
}
